/* eslint-disable max-lines */
// deps
import React, { memo, useCallback, useEffect, useState, useMemo, useRef } from "react";
import cx from "classnames";

// internal deps
import { ApiFetchState } from "@swiggy-private/payment-core";
import { JuspayTokenizationMeta } from "@swiggy-private/payment-types";
import { isJioHotstarFlow } from "@swiggy-private/checkout-core";

// interfaces
import { CardPaymentMethodData, ICardPaymentGroup, NCEMIEvents } from "@payment-ui/interfaces/Card";
import { PaymentMethodProps } from "@payment-ui/interfaces";
import { CARD_PAYMENT_ICON_WIDTH, DEFAULT_CARD_DATA_STATE, FLOW_TYPE } from "./constants";

// widgets
import PaymentMethodIcon from "@payment-ui/widgets/payment-method-icon/PaymentMethodIcon";
import PaymentMethodName from "@payment-ui/widgets/payment-method-name/PaymentMethodName";

// components
import CardBalance from "./components/CardBalance";
import PayButtonV2 from "./components/PayButtonV2";
import PayButton from "./components/PayButton";
import AlertMessage from "./components/AlertMessage";
import { SecureCardInfo } from "@payment-ui/components/secure-card-info";
import { CheckboxWrapper } from "./components/CheckboxWrapper";
import CvvMessage from "./components/CvvMessage";

// helpers
import {
    canEnablePaymentMethod,
    getPaymentMethodDiscount,
    getPaymentMethodDiscounts,
    getPaymentMethodIconUrl,
    getPaymentMethodPromoMsg,
} from "@payment-ui/helpers/Payment";
import {
    isFoodCard,
    getCardMethodTestIds,
    isCardMethodSecured,
    getCardDisplayName,
    hasTokeniseSupport,
    getCardBrand,
    getAriaCardDisplay,
} from "@payment-ui/helpers/Card";
import { getCardPaymentMethodSubName } from "@payment-ui/helpers/TurboPayment";
import { getCvvMessage, getCvvRequired } from "@payment-ui/helpers/Cvv";
import { capitalizeFirstLetter } from "@payment-ui/helpers/String";
import { useCardHalfCard } from "@payment-ui/hooks/useCardHalfCardActions";
import { getPaymentParams } from "./helpers";
import { getPaymentAmount } from "@payment-ui/helpers/Payment";

import styles from "./Card.v4.scss";

export interface SavedCardProps extends PaymentMethodProps {
    methodData: CardPaymentMethodData;
    paymentActions: ICardPaymentGroup["paymentActions"];
    paymentAmount: string;
    juspayTokenizationMeta?: JuspayTokenizationMeta;
    payCtaText?: string;
    payCtaClassName?: string;
    NCEMIEvents?: NCEMIEvents;
    isPreferredPaymentCard?: boolean;
}

function CardPaymentMethod(props: SavedCardProps): React.ReactElement {
    // props
    const {
        onSelect,
        onPay,
        getCardAmount,
        onSecureCardInfoClickAction,
        onKnowMoreTokenizationClickAction,
        onSecureCardInfoView,
        onSaveCardClick,
        onHideCvv,
    } = props.paymentActions;
    const { isPreferredPaymentCard } = props;

    const foodCard = isFoodCard(props.methodData);

    const isMethodEnabled = useMemo(
        () => canEnablePaymentMethod(props.methodData, props.isPaymentGroupEnabled),
        [props.isPaymentGroupEnabled, props.methodData],
    );

    // Tokenization Data
    const isCardSecured = useMemo(() => isCardMethodSecured(props.methodData.meta), [props.methodData.meta]);
    const isTokeniseSupported = useMemo(() => hasTokeniseSupport(props.methodData.meta), [props.methodData.meta]);

    const cardBrand = useMemo(() => getCardBrand(props.methodData.meta), [props.methodData.meta]);

    // states- food card
    const [isBalanceLoading, setBalanceLoading] = useState(foodCard);
    const [cardData, setCardData] = useState(DEFAULT_CARD_DATA_STATE);
    // states- saved card
    const [saveCard, setSaveCard] = useState(false);

    const cardRootRef = useRef<HTMLDivElement>(null);
    const wasPreferredInlineCvvRef = useRef(false);

    const { showSecureCardInfo, onSecureCardInfoClick, closeSecureCardInfo, showSecureCardHalfCardWithActions } =
        useCardHalfCard({
            onSecureCardInfoClickAction,
            onKnowMoreTokenizationClickAction,
        });

    const handleSaveCardClick = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            typeof onSaveCardClick === "function" && onSaveCardClick(!saveCard);
            setSaveCard(!saveCard);
        },
        [onSaveCardClick, saveCard],
    );

    // CVV not required for sodexo cards
    const isCvvRequired = useMemo(
        () => getCvvRequired(props.methodData, cardData),
        [cardData, props.methodData],
    );

    const cvvMessage = useMemo(() => getCvvMessage(props.methodData), [props.methodData]);

    const onClick = useCallback(
        (e: React.MouseEvent): void => {
            e.preventDefault();
            isMethodEnabled && onSelect(props.methodData);
        },
        [onSelect, props.methodData, isMethodEnabled],
    );

    const proceedToPay = useCallback(
        (cvv: string, flowType: FLOW_TYPE) => {
            return (): void => {
                onPay(props.methodData, {
                    cvv,
                    cardBrand,
                    ...getPaymentParams(flowType),
                });
            };
        },
        [cardBrand, onPay, props.methodData],
    );

    const onPayAction = useCallback(
        (cvv: string) => {
            if (isCardSecured) {
                onPay(props.methodData, { cvv, ...getPaymentParams(FLOW_TYPE.SECURED_CARD) });
            } else {
                if (isTokeniseSupported) {
                    if (!saveCard) {
                        showSecureCardHalfCardWithActions(
                            proceedToPay(cvv, FLOW_TYPE.SECURED_CARD),
                            proceedToPay(cvv, FLOW_TYPE.NOT_TOKENIZED_CARD),
                        );
                        return;
                    }
                    onPay(props.methodData, { cvv, ...getPaymentParams(FLOW_TYPE.SECURED_CARD) });
                } else {
                    onPay(props.methodData, { cvv, ...getPaymentParams(FLOW_TYPE.NOT_TOKENIZED_CARD) });
                }
            }
        },
        [
            isCardSecured,
            isTokeniseSupported,
            onPay,
            props.methodData,
            saveCard,
            showSecureCardHalfCardWithActions,
            proceedToPay,
        ],
    );

    useEffect(() => {
        // return if not a food card
        if (!isBalanceLoading) {
            return;
        }

        // return if balance is in loading state but method not present
        if (!getCardAmount) {
            setBalanceLoading(false);
            return;
        }

        const val = getCardAmount(props.methodData);
        if (!val) {
            setBalanceLoading(false);
            return;
        }

        const [state, data] = val;
        if (state === ApiFetchState.SUCCESS) {
            setCardData(data);
            setBalanceLoading(false);
        } else if (state === ApiFetchState.ERROR) {
            setBalanceLoading(false);
        }
    }, [isBalanceLoading, getCardAmount, props.methodData]);

    const hasSufficentBalance = useMemo(() => cardData.isBalanceSufficent, [cardData]);

    const testIds = useMemo(() => getCardMethodTestIds(props.methodData), [props.methodData]);

    const isJioHotstarPaymentFlow = isJioHotstarFlow();

    const containerClass = useMemo(
        () =>
            cx(styles.paymentMethod, {
                [styles.paymentMethodDisabled]: !hasSufficentBalance || !isMethodEnabled,
                [styles.paymentMethodPreferredScrollMargin]: Boolean(isPreferredPaymentCard) && isJioHotstarPaymentFlow,
            }),
        [hasSufficentBalance, isMethodEnabled, isPreferredPaymentCard, isJioHotstarPaymentFlow],
    );

    const paymentAmountSavedCard = getPaymentAmount(props.paymentAmount, props.methodData);
    const hasCTAList = Array.isArray(props.methodData.meta.cta) && props.methodData.meta.cta.length > 0;
    const PayButtonFinal = hasCTAList ? PayButtonV2 : PayButton;

    useEffect(() => {
        const inlineCvvWithAutofocus =
            Boolean(isPreferredPaymentCard) &&
            !hasCTAList &&
            props.selected &&
            !isBalanceLoading &&
            hasSufficentBalance &&
            isCvvRequired;

        const becameTrue = inlineCvvWithAutofocus && !wasPreferredInlineCvvRef.current;
        wasPreferredInlineCvvRef.current = inlineCvvWithAutofocus;

        if (!becameTrue || typeof window === "undefined" || !isJioHotstarFlow()) {
            return;
        }

        let raf2 = 0;
        const raf1 = requestAnimationFrame(() => {
            raf2 = requestAnimationFrame(() => {
                const el = cardRootRef.current;
                if (!el) {
                    return;
                }
                const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
                el.scrollIntoView({
                    behavior: reducedMotion ? "auto" : "smooth",
                    block: "start",
                    inline: "nearest",
                });
            });
        });
        return () => {
            cancelAnimationFrame(raf1);
            cancelAnimationFrame(raf2);
        };
    }, [isPreferredPaymentCard, hasCTAList, props.selected, isBalanceLoading, hasSufficentBalance, isCvvRequired]);

    return (
        <div ref={cardRootRef} className={containerClass} onClick={onClick} data-testid={testIds.CONTAINER}>
            <div className={styles.paymentMethodContainer}>
                <PaymentMethodIcon
                    src={getPaymentMethodIconUrl(props.methodData)}
                    width={CARD_PAYMENT_ICON_WIDTH}
                    data-testid={testIds.ICON}
                />

                <PaymentMethodName
                    displayName={getCardDisplayName(props.methodData.displayName, props.methodData.meta)}
                    promoMsg={getPaymentMethodPromoMsg(props.methodData)}
                    discount={getPaymentMethodDiscount(props.methodData)}
                    discounts={getPaymentMethodDiscounts(props.methodData)}
                    ariaLabel={getAriaCardDisplay(props.methodData.displayName, props.methodData.meta)}
                    subName={getCardPaymentMethodSubName({
                        displayName: props.methodData?.displayName,
                        meta: props.methodData?.meta,
                        isTurboPaymentMethod: false,
                    })}
                    data-testid={testIds.NAME}
                    enabled={isMethodEnabled}
                >
                    <AlertMessage
                        cardData={cardData}
                        hasSufficentBalance={hasSufficentBalance}
                        isBalanceLoading={isBalanceLoading}
                        isMethodEnabled={isMethodEnabled}
                        methodData={props.methodData}
                    />
                </PaymentMethodName>

                {isMethodEnabled && (
                    <CardBalance
                        methodData={props.methodData}
                        selected={props.selected}
                        isBalanceLoading={isBalanceLoading}
                        cardBalance={cardData.balance}
                        hasLessBalance={!hasSufficentBalance}
                    />
                )}
            </div>
            {!isBalanceLoading && hasSufficentBalance && props.selected && (
                <PayButtonFinal
                    enabled={isMethodEnabled}
                    methodData={props.methodData}
                    onPay={onPayAction}
                    paymentAmount={paymentAmountSavedCard}
                    cvvRequired={isCvvRequired}
                    payCtaText={props.payCtaText}
                    payCtaClassName={props.payCtaClassName}
                    onHideCvv={onHideCvv}
                    onPayInEMIClick={props.NCEMIEvents?.onPayInEMIClickAction}
                />
            )}

            <CheckboxWrapper
                {...props.methodData.meta}
                isSelected={props.selected}
                saveCard={saveCard}
                handleSecureCardClick={handleSaveCardClick}
                onSecureCardInfoClick={onSecureCardInfoClick}
            />

            <CvvMessage message={cvvMessage} selected={props.selected} />

            {showSecureCardInfo.show && props.juspayTokenizationMeta && (
                <SecureCardInfo
                    data={props.juspayTokenizationMeta}
                    onClose={closeSecureCardInfo}
                    cardBrand={capitalizeFirstLetter(cardBrand)}
                    {...showSecureCardInfo.actions}
                    onLoad={onSecureCardInfoView}
                />
            )}
        </div>
    );
}

export default memo(CardPaymentMethod);
